import { Button, makeStyles, Skeleton, SkeletonItem } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import Status from "../../../enums/Status";
import { dolphinTokens } from "../../../utils/Theme";
import ICaseStudyPreview from "../../../models/caseStudy/ICaseStudyPreview";

interface IIndustriesProps {
    caseStudies: ICaseStudyPreview[];
    status: Status;
    selectedIndustries: string[];
    setSelectedIndustries: (industries: string[]) => void;
}

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexWrap: "wrap",
        rowGap: "8px",
        columnGap: "8px",
        padding: "32px",
        maxWidth: "100%",
        justifyContent: "center",
        margin: "0 auto",
        backgroundColor: dolphinTokens.colorLightBlue
    },
    skeletons: {
        display: "flex",
        flexDirection: "row",
        columnGap: "12px",
        rowGap: "8px",
    },
    skeleton: {
        width: "96px",
        height: "32px",
    },
    skeletonItem: {
        height: "100%",
        width: "100%",
        borderRadius: "10000px !important",
    },
    unselected: {
        color: "white !important",
    }
});

export default function Industries(props: IIndustriesProps) {
    const styles = useStyles();

    const [industries, setIndustries] = useState<string[]>([]);

    useEffect(() => {
        if (props.status !== Status.LOAD_SUCCESS) {
            return;
        }
        
        const industriesList = getIndustries(props.caseStudies);
        setIndustries(industriesList);
    }, [props.status]);

    const handleIndustryChange = (industry: string) => {
        const selected = props.selectedIndustries.includes(industry);
        const newSelectedIndustries = selected
            ? props.selectedIndustries.filter((c) => c !== industry)
            : [...props.selectedIndustries, industry];

        props.setSelectedIndustries(newSelectedIndustries);
    };
    
    return (
        <div className={styles.container}>
            {
                props.status === Status.LOADING &&
                <div className={styles.skeletons}>
                    {
                        Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton className={styles.skeleton} key={index}>
                                <SkeletonItem className={styles.skeletonItem} shape="rectangle" />
                            </Skeleton>
                        ))
                    }
                </div>
            }

            {
                props.status === Status.LOAD_SUCCESS && industries.length > 0 &&
                industries
                    .sort((a, b) => a.localeCompare(b))
                    .map((industry, index) => {
                        const isSelected = props.selectedIndustries.includes(industry);
                        const btnAppearance = isSelected ? "secondary" : "outline";
                        
                        return (
                            <Button
                                key={index}
                                shape="circular"
                                appearance={btnAppearance}
                                onClick={() => handleIndustryChange(industry)}
                                className={isSelected ? "" : styles.unselected}
                                >
                                    {industry}
                            </Button>
                        );
                    })
            }
        </div>
    );
}

function getIndustries(caseStudies: ICaseStudyPreview[]) {
    const industries = new Set<string>();
    caseStudies.forEach(c => industries.add(c.client.industry));

    return Array.from(industries);
}